/**
 * Admin — Manutenção de Packs (produto.Tinf_Pack).
 *
 * Lista + Editor de packs com composição de produtos.
 * URL: /admin/packs
 */
import { useCallback, useEffect, useState } from "react";
import {
  Package, Search, Plus, Loader2, Pencil, Save, X, RefreshCcw,
  CheckCircle2, XCircle, Trash2,
} from "lucide-react";
import { adminApi } from "../api";
import { toast } from "sonner";

const empty_pack = {
  Descricao: "", ID_TipoPack: "SB", ID_Iva: 1, ID_Periodicidade: null,
  PrecoBaseClienteFinal: 0, PrecoBaseConsultor: 0,
  PrecoBaseConsultorDescontoReduzido: 0, PrecoBaseAfiliado: 0,
  NetPoints: 0, Creditos: 0, Quantidade: 99999999, QuantidadeMinima: 0,
  DisponivelWebsite: "N", DisponivelMobile: "N",
  CancelaAcessoFinalSubscricao: "N", Activo: "S", ID_Moeda: "EUR",
  Observacoes: "",
};

export default function AdminPacks() {
  const [rows, setRows] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState("");
  const [filterTipo, setFilterTipo] = useState("");
  const [filterActivo, setFilterActivo] = useState("");
  const [dropdowns, setDropdowns] = useState({ tipos_pack: [], periodicidades: [], ivas: [] });
  const [editing, setEditing] = useState(null); // pack detail or "new"
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await adminApi.packsList({ q, tipo: filterTipo, activo: filterActivo, limit: 200 });
      setRows(r.data || []);
      setTotal(r.total || 0);
    } catch (e) { toast.error(e.message || "Erro"); }
    finally { setLoading(false); }
  }, [q, filterTipo, filterActivo]);

  useEffect(() => {
    adminApi.packsDropdowns().then(setDropdowns).catch(() => {});
  }, []);

  useEffect(() => {
    const t = setTimeout(load, 300);
    return () => clearTimeout(t);
  }, [load]);

  const openEdit = async (id) => {
    if (id === "new") { setEditing({ isNew: true, pack: { ...empty_pack }, produtos: [] }); return; }
    try {
      const r = await adminApi.packGet(id);
      setEditing({ isNew: false, pack: r.pack, produtos: r.produtos || [] });
    } catch (e) { toast.error(e.message || "Erro"); }
  };

  const toggleActive = async (id) => {
    try {
      const r = await adminApi.packToggle(id);
      toast.success(`Pack ${r.activo === "S" ? "activado" : "desactivado"}`);
      load();
    } catch (e) { toast.error(e.message || "Erro"); }
  };

  return (
    <div className="p-6 max-w-7xl mx-auto" data-testid="admin-packs-page">
      <div className="flex items-center gap-3 mb-4">
        <Package className="w-6 h-6 text-[#0F1829]" />
        <h1 className="text-2xl font-semibold text-[#111827]">Manutenção de Packs</h1>
        <span className="text-sm text-[#6B7280] ml-2">{total} pack(s)</span>
      </div>

      {/* Filters + New */}
      <div className="flex flex-col sm:flex-row gap-3 mb-4">
        <div className="relative flex-1">
          <Search className="w-4 h-4 absolute left-3 top-3 text-[#9CA3AF]" />
          <input
            data-testid="packs-search"
            value={q} onChange={e => setQ(e.target.value)}
            placeholder="ID ou descrição..."
            className="w-full pl-9 pr-3 py-2 border border-[#D1D5DB] rounded-md text-sm focus:ring-2 focus:ring-[#0F1829]/30 focus:border-[#0F1829] outline-none"
          />
        </div>
        <select
          data-testid="packs-filter-tipo"
          value={filterTipo} onChange={e => setFilterTipo(e.target.value)}
          className="px-3 py-2 border border-[#D1D5DB] rounded-md text-sm bg-white"
        >
          <option value="">Todos os tipos</option>
          {dropdowns.tipos_pack.map(t => (
            <option key={t.ID_TipoPack} value={t.ID_TipoPack}>{t.Descricao}</option>
          ))}
        </select>
        <select
          data-testid="packs-filter-activo"
          value={filterActivo} onChange={e => setFilterActivo(e.target.value)}
          className="px-3 py-2 border border-[#D1D5DB] rounded-md text-sm bg-white"
        >
          <option value="">Todos</option>
          <option value="S">Activos</option>
          <option value="N">Inactivos</option>
        </select>
        <button
          data-testid="packs-refresh"
          onClick={load}
          className="flex items-center gap-1 px-3 py-2 border border-[#D1D5DB] rounded-md text-sm hover:bg-[#F9FAFB]"
        >
          <RefreshCcw className="w-4 h-4" />
        </button>
        <button
          data-testid="packs-new-btn"
          onClick={() => openEdit("new")}
          className="flex items-center gap-1 px-4 py-2 bg-[#0F1829] text-white rounded-md text-sm hover:bg-[#001f6e]"
        >
          <Plus className="w-4 h-4" /> Novo Pack
        </button>
      </div>

      {/* Table */}
      {loading ? (
        <div className="flex justify-center py-16"><Loader2 className="w-6 h-6 animate-spin text-[#6B7280]" /></div>
      ) : (
        <div className="bg-white border border-[#E5E7EB] rounded-lg overflow-x-auto">
          <table className="w-full text-sm min-w-[640px]">
            <thead className="bg-[#F9FAFB] border-b border-[#E5E7EB]">
              <tr>
                <th className="px-3 py-2 text-left font-medium text-[#6B7280] text-xs uppercase">ID</th>
                <th className="px-3 py-2 text-left font-medium text-[#6B7280] text-xs uppercase">Descrição</th>
                <th className="px-3 py-2 text-left font-medium text-[#6B7280] text-xs uppercase">Tipo</th>
                <th className="px-3 py-2 text-right font-medium text-[#6B7280] text-xs uppercase">Preço CF</th>
                <th className="px-3 py-2 text-right font-medium text-[#6B7280] text-xs uppercase">Preço CS</th>
                <th className="px-3 py-2 text-center font-medium text-[#6B7280] text-xs uppercase">Produtos</th>
                <th className="px-3 py-2 text-center font-medium text-[#6B7280] text-xs uppercase">Activo</th>
                <th className="px-3 py-2 text-right font-medium text-[#6B7280] text-xs uppercase">Acções</th>
              </tr>
            </thead>
            <tbody>
              {rows.map(r => (
                <tr key={r.ID_Pack} className="border-b border-[#F3F4F6] hover:bg-[#F9FAFB]" data-testid={`pack-row-${r.ID_Pack}`}>
                  <td className="px-3 py-2 font-mono text-[#6B7280]">{r.ID_Pack}</td>
                  <td className="px-3 py-2 text-[#111827]">{r.Descricao}</td>
                  <td className="px-3 py-2 text-[#6B7280] text-xs">{r.TipoPackNome || r.ID_TipoPack}</td>
                  <td className="px-3 py-2 text-right text-[#111827]">{(r.PrecoBaseClienteFinal || 0).toFixed(2)}€</td>
                  <td className="px-3 py-2 text-right text-[#111827]">{(r.PrecoBaseConsultor || 0).toFixed(2)}€</td>
                  <td className="px-3 py-2 text-center text-[#6B7280]">{r.NumProdutos}</td>
                  <td className="px-3 py-2 text-center">
                    {r.Activo === "S"
                      ? <CheckCircle2 className="w-4 h-4 text-green-600 inline" />
                      : <XCircle className="w-4 h-4 text-red-500 inline" />}
                  </td>
                  <td className="px-3 py-2 text-right space-x-1">
                    <button
                      data-testid={`pack-edit-${r.ID_Pack}`}
                      onClick={() => openEdit(r.ID_Pack)}
                      className="p-1.5 rounded hover:bg-[#E5E7EB] text-[#6B7280]"
                      title="Editar"
                    ><Pencil className="w-4 h-4" /></button>
                    <button
                      data-testid={`pack-toggle-${r.ID_Pack}`}
                      onClick={() => toggleActive(r.ID_Pack)}
                      className="p-1.5 rounded hover:bg-[#E5E7EB] text-[#6B7280]"
                      title={r.Activo === "S" ? "Desactivar" : "Activar"}
                    >
                      {r.Activo === "S"
                        ? <XCircle className="w-4 h-4 text-red-500" />
                        : <CheckCircle2 className="w-4 h-4 text-green-600" />}
                    </button>
                  </td>
                </tr>
              ))}
              {rows.length === 0 && (
                <tr><td colSpan="8" className="text-center py-8 text-[#9CA3AF]">Sem resultados.</td></tr>
              )}
            </tbody>
          </table>
        </div>
      )}

      {editing && (
        <PackEditor
          state={editing} setState={setEditing}
          dropdowns={dropdowns} saving={saving} setSaving={setSaving}
          onClose={() => setEditing(null)}
          onSaved={() => { setEditing(null); load(); }}
        />
      )}
    </div>
  );
}

/* ────────────────────────────────────────────────────────── */
/*  Editor modal — Detalhes + Composição                      */
/* ────────────────────────────────────────────────────────── */

function PackEditor({ state, setState, dropdowns, saving, setSaving, onClose, onSaved }) {
  const { pack, produtos, isNew } = state;
  const [tab, setTab] = useState("detalhes");
  const [newProdId, setNewProdId] = useState("");

  const setField = (k, v) => setState(s => ({ ...s, pack: { ...s.pack, [k]: v } }));

  const save = async () => {
    if (!pack.Descricao?.trim()) { toast.error("Descrição obrigatória"); return; }
    setSaving(true);
    try {
      if (isNew) {
        const r = await adminApi.packCreate(pack);
        toast.success(`Pack #${r.id_pack} criado`);
      } else {
        await adminApi.packUpdate(pack.ID_Pack, pack);
        toast.success("Pack actualizado");
      }
      onSaved();
    } catch (e) { toast.error(e.message || "Erro ao gravar"); }
    finally { setSaving(false); }
  };

  const addProduct = async () => {
    const id = parseInt(newProdId, 10);
    if (!id) { toast.error("ID de produto inválido"); return; }
    try {
      await adminApi.packAddProduct(pack.ID_Pack, id);
      toast.success("Produto adicionado");
      setNewProdId("");
      const r = await adminApi.packGet(pack.ID_Pack);
      setState(s => ({ ...s, produtos: r.produtos || [] }));
    } catch (e) { toast.error(e.message || "Erro"); }
  };

  const removeProduct = async (idPackProduto) => {
    if (!window.confirm("Remover este produto do pack?")) return;
    try {
      await adminApi.packRemoveProduct(pack.ID_Pack, idPackProduto);
      const r = await adminApi.packGet(pack.ID_Pack);
      setState(s => ({ ...s, produtos: r.produtos || [] }));
    } catch (e) { toast.error(e.message || "Erro"); }
  };

  return (
    <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4" onClick={onClose}>
      <div
        data-testid="pack-editor-modal"
        className="bg-white rounded-xl shadow-xl w-full max-w-3xl max-h-[92vh] overflow-hidden flex flex-col"
        onClick={e => e.stopPropagation()}
      >
        <div className="flex items-center justify-between px-6 py-4 border-b border-[#E5E7EB]">
          <h3 className="text-lg font-semibold text-[#111827]">
            {isNew ? "Novo Pack" : `Editar Pack #${pack.ID_Pack} — ${pack.Descricao}`}
          </h3>
          <button onClick={onClose} className="p-1 rounded hover:bg-[#F3F4F6] text-[#6B7280]" data-testid="pack-editor-close">
            <X className="w-5 h-5" />
          </button>
        </div>

        {!isNew && (
          <div className="flex border-b border-[#E5E7EB] px-4 bg-[#F9FAFB]">
            <TabBtn active={tab === "detalhes"} onClick={() => setTab("detalhes")}>Detalhes</TabBtn>
            <TabBtn active={tab === "composicao"} onClick={() => setTab("composicao")}>Composição ({produtos.length})</TabBtn>
          </div>
        )}

        <div className="p-6 overflow-y-auto flex-1 space-y-4">
          {(isNew || tab === "detalhes") && (
            <>
              <Field label="Descrição">
                <input
                  data-testid="pack-descricao"
                  type="text" value={pack.Descricao || ""}
                  onChange={e => setField("Descricao", e.target.value)}
                  className={inputClass}
                />
              </Field>
              <div className="grid grid-cols-2 gap-4">
                <Field label="Tipo de Pack">
                  <select data-testid="pack-tipo" value={pack.ID_TipoPack || ""} onChange={e => setField("ID_TipoPack", e.target.value)} className={inputClass}>
                    {dropdowns.tipos_pack.map(t => <option key={t.ID_TipoPack} value={t.ID_TipoPack}>{t.Descricao}</option>)}
                  </select>
                </Field>
                <Field label="Periodicidade (subscrições)">
                  <select data-testid="pack-periodicidade" value={pack.ID_Periodicidade || ""} onChange={e => setField("ID_Periodicidade", e.target.value ? parseInt(e.target.value) : null)} className={inputClass}>
                    <option value="">— sem subscrição —</option>
                    {dropdowns.periodicidades.map(p => <option key={p.ID_Periodicidade} value={p.ID_Periodicidade}>{p.Descricao}</option>)}
                  </select>
                </Field>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <Field label="Preço CF (€)"><NumInput testid="pack-preco-cf" val={pack.PrecoBaseClienteFinal} onChange={v => setField("PrecoBaseClienteFinal", v)} /></Field>
                <Field label="Preço CS (€)"><NumInput testid="pack-preco-cs" val={pack.PrecoBaseConsultor} onChange={v => setField("PrecoBaseConsultor", v)} /></Field>
                <Field label="Preço CS Reduzido (€)"><NumInput testid="pack-preco-cs-red" val={pack.PrecoBaseConsultorDescontoReduzido} onChange={v => setField("PrecoBaseConsultorDescontoReduzido", v)} /></Field>
                <Field label="Preço Afiliado (€)"><NumInput testid="pack-preco-af" val={pack.PrecoBaseAfiliado} onChange={v => setField("PrecoBaseAfiliado", v)} /></Field>
                <Field label="NetPoints"><NumInput testid="pack-netpoints" val={pack.NetPoints} onChange={v => setField("NetPoints", v)} /></Field>
                <Field label="Créditos"><NumInput testid="pack-creditos" val={pack.Creditos} onChange={v => setField("Creditos", v)} /></Field>
                <Field label="IVA">
                  <select data-testid="pack-iva" value={pack.ID_Iva || 1} onChange={e => setField("ID_Iva", parseInt(e.target.value))} className={inputClass}>
                    {dropdowns.ivas.map(i => <option key={i.ID_Iva} value={i.ID_Iva}>{i.Descricao} ({i.Valor}%)</option>)}
                  </select>
                </Field>
                <Field label="Referência">
                  <input data-testid="pack-referencia" type="text" value={pack.Referencia || ""} onChange={e => setField("Referencia", e.target.value)} className={inputClass} />
                </Field>
              </div>
              <div className="grid grid-cols-3 gap-4 pt-2 border-t border-[#F3F4F6]">
                <Toggle testid="pack-web" label="Disponível Website" val={pack.DisponivelWebsite} onChange={v => setField("DisponivelWebsite", v)} />
                <Toggle testid="pack-mobile" label="Disponível Mobile" val={pack.DisponivelMobile} onChange={v => setField("DisponivelMobile", v)} />
                <Toggle testid="pack-cancela" label="Cancela Acesso no Fim" val={pack.CancelaAcessoFinalSubscricao} onChange={v => setField("CancelaAcessoFinalSubscricao", v)} />
                <Toggle testid="pack-activo" label="Activo" val={pack.Activo} onChange={v => setField("Activo", v)} />
              </div>
              <Field label="Observações (HTML)">
                <textarea data-testid="pack-observacoes" value={pack.Observacoes || ""} onChange={e => setField("Observacoes", e.target.value)} rows={4} className={inputClass + " font-mono text-xs"} />
              </Field>
            </>
          )}

          {!isNew && tab === "composicao" && (
            <div className="space-y-3">
              <div className="flex gap-2">
                <input
                  data-testid="pack-add-produto-id"
                  type="number" value={newProdId} onChange={e => setNewProdId(e.target.value)}
                  placeholder="ID do produto a adicionar"
                  className={inputClass}
                />
                <button
                  data-testid="pack-add-produto-btn"
                  onClick={addProduct}
                  className="flex items-center gap-1 px-4 py-2 bg-[#0F1829] text-white rounded-md text-sm hover:bg-[#001f6e]"
                >
                  <Plus className="w-4 h-4" /> Adicionar
                </button>
              </div>
              <div className="border border-[#E5E7EB] rounded-lg overflow-x-auto">
                <table className="w-full text-sm min-w-[640px]">
                  <thead className="bg-[#F9FAFB]">
                    <tr>
                      <th className="px-3 py-2 text-left font-medium text-[#6B7280] text-xs">ID</th>
                      <th className="px-3 py-2 text-left font-medium text-[#6B7280] text-xs">Descrição</th>
                      <th className="px-3 py-2 text-left font-medium text-[#6B7280] text-xs">Tipo</th>
                      <th className="px-3 py-2 text-right font-medium text-[#6B7280] text-xs">Acções</th>
                    </tr>
                  </thead>
                  <tbody>
                    {produtos.map(p => (
                      <tr key={p.ID_PackProduto} className="border-b border-[#F3F4F6]" data-testid={`pack-prod-${p.ID_Produto}`}>
                        <td className="px-3 py-2 font-mono text-[#6B7280]">{p.ID_Produto}</td>
                        <td className="px-3 py-2 text-[#111827]">{p.Descricao}</td>
                        <td className="px-3 py-2 text-[#6B7280] text-xs">{p.ID_TipoProduto}</td>
                        <td className="px-3 py-2 text-right">
                          <button
                            data-testid={`pack-prod-remove-${p.ID_Produto}`}
                            onClick={() => removeProduct(p.ID_PackProduto)}
                            className="p-1 rounded hover:bg-red-50 text-red-500"
                          ><Trash2 className="w-4 h-4" /></button>
                        </td>
                      </tr>
                    ))}
                    {produtos.length === 0 && (
                      <tr><td colSpan="4" className="text-center py-6 text-[#9CA3AF]">Sem produtos.</td></tr>
                    )}
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </div>

        <div className="flex justify-end gap-3 px-6 py-4 border-t border-[#E5E7EB] bg-[#F9FAFB]">
          <button onClick={onClose} className="px-4 py-2 text-sm text-[#6B7280] hover:text-[#111827]" data-testid="pack-editor-cancel">Cancelar</button>
          {(isNew || tab === "detalhes") && (
            <button
              data-testid="pack-editor-save"
              onClick={save} disabled={saving}
              className="flex items-center gap-1 px-5 py-2 bg-[#0F1829] text-white rounded-md text-sm hover:bg-[#001f6e] disabled:opacity-50"
            >
              {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
              Gravar
            </button>
          )}
        </div>
      </div>
    </div>
  );
}

/* ────────── helpers ────────── */

const inputClass =
  "w-full px-3 py-2 border border-[#D1D5DB] rounded-md text-sm focus:ring-2 focus:ring-[#0F1829]/30 focus:border-[#0F1829] outline-none bg-white";

const TabBtn = ({ active, onClick, children }) => (
  <button
    onClick={onClick}
    className={`px-4 py-2.5 text-sm font-medium border-b-2 transition-colors ${
      active ? "border-[#0F1829] text-[#0F1829]" : "border-transparent text-[#6B7280] hover:text-[#111827]"
    }`}
  >{children}</button>
);

const Field = ({ label, children }) => (
  <div>
    <label className="block text-xs font-medium text-[#6B7280] mb-1">{label}</label>
    {children}
  </div>
);

const NumInput = ({ val, onChange, testid }) => (
  <input
    data-testid={testid}
    type="number" step="0.01"
    value={val ?? 0}
    onChange={e => onChange(parseFloat(e.target.value) || 0)}
    className={inputClass}
  />
);

const Toggle = ({ label, val, onChange, testid }) => (
  <label className="flex items-center gap-2 text-sm text-[#374151] cursor-pointer">
    <input
      data-testid={testid}
      type="checkbox" checked={val === "S"}
      onChange={e => onChange(e.target.checked ? "S" : "N")}
      className="w-4 h-4"
    />
    {label}
  </label>
);
